import { useCanMessageMember } from "@/react/components/Profile/useCanMessageMember";
import { classNames } from "@/react/helpers/twMergeWithCN";
import { useCircleV4 } from "@/react/hooks/useCircleV4";
import {
  useCanCreateConnection,
  useIsConnectEnabled,
} from "@/react/hooks/useIsConnectEnabled";

import { AcceptIgnoreButtons } from "./AcceptIgnoreButtons";
import { ActiveStateButton } from "./ActiveStateButton";
import { ConnectButton } from "./ConnectButton";
import { useConnectionButtonState } from "./hooks/useConnectionButtonState";
import { PendingButton } from "./PendingButton";
import type { ConnectionButtonsOrchestratorProps } from "./types";
import { ViewProfileButton } from "./ViewProfileButton";
export const ConnectionButtonsOrchestrator = ({
  member,
  variant = "default",
  size = "md",
  className,
  showMomentumModal = true,
  acceptButtonPosition = "right",
  acceptIgnoreIconOnly = false,
  activeStateDisplay = "message",
  closeParent,
  recommendationId,
  canReceiveDmFromCurrentMember,
  fullWidth = false,
  iconOnly = false,
}: ConnectionButtonsOrchestratorProps) => {
  const { isCircleV4 } = useCircleV4();
  const isConnectEnabled = useIsConnectEnabled();
  const canCreateConnection = useCanCreateConnection();
  const { shouldShowMessageOption } = useCanMessageMember();
  const shouldShowMessageCTA =
    canReceiveDmFromCurrentMember ?? shouldShowMessageOption(member);

  const {
    isCurrentUser,
    isEmpty,
    isPendingSent,
    isPendingReceived,
    isActive,
    connection,
  } = useConnectionButtonState(member);

  if (!member || !member.name || !member.public_uid || !member.first_name) {
    return null;
  }

  const v4OrV3Props = isCircleV4 ? { fullWidth } : { variant, size };
  const viewProfileProps = isCircleV4 ? { fullWidth } : { variant, size };

  if (isCurrentUser) {
    return (
      <div
        className={classNames(
          "flex flex-col",
          isCircleV4 && fullWidth ? "w-full" : "w-fit",
          className,
        )}
      >
        <ViewProfileButton member={member} {...viewProfileProps} />
      </div>
    );
  }

  const activeStateButton = (
    <ActiveStateButton
      activeStateDisplay={activeStateDisplay}
      closeParent={closeParent}
      fullWidth={fullWidth}
      iconOnly={iconOnly}
      isCircleV4={isCircleV4}
      isConnectEnabled={isConnectEnabled}
      member={member}
      shouldShowMessageCTA={shouldShowMessageCTA}
      size={size}
      variant={variant}
    />
  );

  if (!isConnectEnabled) {
    return (
      <div className={classNames("flex w-fit flex-col", className)}>
        {activeStateButton}
      </div>
    );
  }

  return (
    <div
      className={classNames(
        "flex flex-col",
        isCircleV4 && fullWidth ? "w-full" : "w-fit",
        className,
      )}
    >
      {isEmpty && canCreateConnection && (
        <ConnectButton
          recipient={member}
          recommendationId={recommendationId}
          {...v4OrV3Props}
        />
      )}

      {isPendingSent && (
        <PendingButton
          connectionId={connection?.id?.toString()}
          recipientName={connection?.recipient.name ?? ""}
          memberPublicUid={member.public_uid}
          {...v4OrV3Props}
        />
      )}

      {isPendingReceived && (
        <AcceptIgnoreButtons
          connectionId={connection?.id?.toString()}
          recipient={member}
          showMomentumModal={showMomentumModal}
          acceptButtonPosition={acceptButtonPosition}
          iconOnly={acceptIgnoreIconOnly}
          {...(isCircleV4 ? { fullWidth } : { size, className })}
        />
      )}

      {isActive && activeStateButton}
    </div>
  );
};
